import { useEffect, useRef, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
  useActiveTabId,
  useActiveUi,
} from "@/state/hooks";
import {
  clearAllDrawings,
  loadDrawings,
} from "@/state/slices/uiSlice";
import {
  fetchDrawings,
  saveDrawings,
  setActiveName,
} from "@/state/slices/drawingSetsSlice";
import type { SavedDrawingSet } from "@/api/types";
import styles from "./DrawingsDialog.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
}

type FibShape = {
  id: string;
  startTime: number;
  startPrice: number;
  endTime: number;
  endPrice: number;
};

type DiagonalShape = {
  id: string;
  startTime: number;
  startPrice: number;
  endTime: number;
  endPrice: number;
};

type PathShape = {
  id: string;
  points: ReadonlyArray<{ time: number; price: number }>;
};

type PositionShape = {
  id: string;
  type: "long" | "short";
  entryPrice: number;
  slPrice: number;
  tpPrice: number;
  startTime: number;
  endTime: number;
};

type PriceRangeShape = {
  id: string;
  startTime: number;
  startPrice: number;
  endTime: number;
  endPrice: number;
};

/** Build a SavedDrawingSet snapshot from the current chart drawings. */
function snapshotFromChart(
  fibs: readonly FibShape[],
  hlines: readonly { id: string; price: number }[],
  vlines: readonly { id: string; time: number }[],
  diagonals: readonly DiagonalShape[],
  paths: readonly PathShape[],
  positions: readonly PositionShape[],
  priceRanges: readonly PriceRangeShape[],
): SavedDrawingSet {
  return {
    fibs: fibs.map((f) => ({
      id: f.id,
      startTime: f.startTime,
      startPrice: f.startPrice,
      endTime: f.endTime,
      endPrice: f.endPrice,
    })),
    horizontal_lines: hlines.map((l) => ({ id: l.id, price: l.price })),
    vertical_lines: vlines.map((l) => ({ id: l.id, time: l.time })),
    diagonal_lines: diagonals.map((d) => ({
      id: d.id,
      startTime: d.startTime,
      startPrice: d.startPrice,
      endTime: d.endTime,
      endPrice: d.endPrice,
    })),
    paths: paths.map((p) => ({
      id: p.id,
      points: p.points.map((pt) => ({ time: pt.time, price: pt.price })),
    })),
    positions: positions.map((p) => ({
      id: p.id,
      type: p.type,
      entryPrice: p.entryPrice,
      slPrice: p.slPrice,
      tpPrice: p.tpPrice,
      startTime: p.startTime,
      endTime: p.endTime,
    })),
    price_ranges: priceRanges.map((b) => ({
      id: b.id,
      startTime: b.startTime,
      startPrice: b.startPrice,
      endTime: b.endTime,
      endPrice: b.endPrice,
    })),
  };
}

export function DrawingsDialog({ open, onClose }: Props) {
  const dispatch = useAppDispatch();
  const tabId = useActiveTabId();
  const {
    symbol,
    fibRetracements: fibs,
    horizontalLines: hlines,
    verticalLines: vlines,
    diagonalLines: diagonals,
    paths,
    positions,
    priceRangeBoxes,
  } = useActiveUi();
  const sets = useAppSelector((s) => s.drawingSets.sets);
  const activeName = useAppSelector((s) => s.drawingSets.activeName);
  const status = useAppSelector((s) => s.drawingSets.status);
  const saving = useAppSelector((s) => s.drawingSets.saving);
  const error = useAppSelector((s) => s.drawingSets.error);

  // Input boxes — Save-as creates a *new* set; the inline rename
  // input replaces the active set's key.
  const [newName, setNewName] = useState("");
  const [renameDraft, setRenameDraft] = useState<string | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && symbol) {
      dispatch(fetchDrawings(symbol));
      setNewName("");
      setRenameDraft(null);
    }
  }, [open, symbol, dispatch]);

  // Auto-focus the rename input when the user clicks the active name.
  useEffect(() => {
    if (renameDraft !== null) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [renameDraft]);

  if (!open) return null;

  const totalShown =
    fibs.length +
    hlines.length +
    vlines.length +
    diagonals.length +
    paths.length +
    positions.length +
    priceRangeBoxes.length;
  const currentSnapshot = snapshotFromChart(
    fibs,
    hlines,
    vlines,
    diagonals,
    paths,
    positions,
    priceRangeBoxes,
  );

  // Save (update the active set in place).
  const handleSaveActive = async () => {
    if (!activeName) return;
    const next = { ...sets, [activeName]: currentSnapshot };
    await dispatch(saveDrawings({ symbol, sets: next }));
  };

  // Save-as new (clone): create a new named entry, make it active.
  const handleSaveAsNew = async () => {
    const name = newName.trim();
    if (!name) return;
    const next = { ...sets, [name]: currentSnapshot };
    const result = await dispatch(saveDrawings({ symbol, sets: next }));
    if (saveDrawings.fulfilled.match(result)) {
      dispatch(setActiveName(name));
      setNewName("");
    }
  };

  const handleLoad = (name: string) => {
    const set = sets[name];
    if (!set) return;
    dispatch(loadDrawings({ tabId, ...set }));
    dispatch(setActiveName(name));
    onClose();
  };

  const handleDelete = async (name: string) => {
    const next = { ...sets };
    delete next[name];
    const result = await dispatch(saveDrawings({ symbol, sets: next }));
    if (saveDrawings.fulfilled.match(result) && activeName === name) {
      dispatch(setActiveName(null));
    }
  };

  const handleNew = () => {
    dispatch(clearAllDrawings({ tabId }));
    dispatch(setActiveName(null));
    onClose();
  };

  // Destructive: wipe every saved set on the server for this symbol.
  // The chart drawings are NOT touched — that's what the toolbar's
  // trash button is for.
  const handleDeleteAllSaved = async () => {
    if (Object.keys(sets).length === 0) return;
    const ok = window.confirm(
      `Delete all ${Object.keys(sets).length} saved set(s) for ${symbol}? ` +
        `This can't be undone.`,
    );
    if (!ok) return;
    const result = await dispatch(saveDrawings({ symbol, sets: {} }));
    if (saveDrawings.fulfilled.match(result)) {
      dispatch(setActiveName(null));
    }
  };

  // Rename: replace the dict key while keeping its value.
  const commitRename = async () => {
    if (renameDraft === null || !activeName) {
      setRenameDraft(null);
      return;
    }
    const newKey = renameDraft.trim();
    if (!newKey || newKey === activeName) {
      setRenameDraft(null);
      return;
    }
    if (sets[newKey]) {
      // Don't silently clobber another saved set.
      return;
    }
    const next: Record<string, SavedDrawingSet> = {};
    for (const [k, v] of Object.entries(sets)) {
      next[k === activeName ? newKey : k] = v;
    }
    const result = await dispatch(saveDrawings({ symbol, sets: next }));
    if (saveDrawings.fulfilled.match(result)) {
      dispatch(setActiveName(newKey));
      setRenameDraft(null);
    }
  };

  return (
    <div className={styles.backdrop} onClick={onClose} role="presentation">
      <div
        className={styles.dialog}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className={styles.header}>
          <h2 className={styles.title}>Drawings · {symbol}</h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className={styles.body}>
          {/* Active set bar — only present when something is loaded. */}
          {activeName ? (
            <section className={styles.activeBar}>
              <div className={styles.activeLabel}>Editing</div>
              {renameDraft !== null ? (
                <input
                  ref={renameInputRef}
                  type="text"
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitRename();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setRenameDraft(null);
                    }
                  }}
                  onBlur={commitRename}
                  className={styles.renameInput}
                />
              ) : (
                <button
                  type="button"
                  className={styles.activeName}
                  onClick={() => setRenameDraft(activeName)}
                  title="Click to rename"
                >
                  {activeName}
                </button>
              )}
              <span className={styles.activeStats}>
                ({totalShown} drawing{totalShown !== 1 ? "s" : ""})
              </span>
              <div className={styles.activeActions}>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={handleSaveActive}
                  disabled={saving}
                  title="Overwrite this set with the current chart"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </section>
          ) : (
            <section className={styles.activeBar}>
              <div className={styles.activeLabel}>Unsaved</div>
              <span className={styles.activeStats}>
                {totalShown} drawing{totalShown !== 1 ? "s" : ""} on chart — Save
                as new to keep them
              </span>
            </section>
          )}

          <section className={styles.section}>
            <div className={styles.sectionLabel}>
              {activeName ? "Save as new (clone)" : "Save current"}
            </div>
            <div className={styles.saveRow}>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveAsNew();
                  }
                }}
                placeholder={
                  activeName ? `Clone of ${activeName}…` : "Set name…"
                }
                className={styles.input}
              />
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleSaveAsNew}
                disabled={saving || !newName.trim() || totalShown === 0}
                title={
                  totalShown === 0
                    ? "Nothing on the chart to save"
                    : "Save current drawings under this name"
                }
              >
                {activeName ? "Save as" : "Save"}
              </button>
            </div>
            {newName.trim() && sets[newName.trim()] ? (
              <div className={styles.warn}>
                A set named "{newName.trim()}" exists — it will be overwritten.
              </div>
            ) : null}
          </section>

          <section className={styles.section}>
            <div className={styles.sectionLabel}>
              Saved sets
              {status === "loading" ? (
                <span className={styles.muted}> · loading…</span>
              ) : null}
            </div>
            {Object.keys(sets).length === 0 ? (
              <div className={styles.muted}>
                {status === "succeeded"
                  ? "No saved sets for this symbol yet."
                  : "—"}
              </div>
            ) : (
              <ul className={styles.list}>
                {Object.entries(sets)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([name, set]) => {
                    const pCount = set.positions?.length ?? 0;
                    const dCount = set.diagonal_lines?.length ?? 0;
                    const pathCount = set.paths?.length ?? 0;
                    const count =
                      set.fibs.length +
                      set.horizontal_lines.length +
                      set.vertical_lines.length +
                      dCount +
                      pathCount +
                      pCount;
                    const isActive = name === activeName;
                    return (
                      <li
                        key={name}
                        className={`${styles.listItem} ${isActive ? styles.activeItem : ""}`}
                      >
                        <div className={styles.itemMeta}>
                          <div className={styles.itemName}>
                            {isActive ? "● " : ""}
                            {name}
                          </div>
                          <div className={styles.itemSub}>
                            {set.fibs.length} fib · {set.horizontal_lines.length}{" "}
                            H · {set.vertical_lines.length} V · {dCount} D ·{" "}
                            {pathCount} path · {pCount} pos · {count} total
                          </div>
                        </div>
                        <div className={styles.itemActions}>
                          <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={() => handleLoad(name)}
                            title={
                              isActive
                                ? "Reload this set — discards any unsaved chart edits"
                                : "Replace chart drawings with this set"
                            }
                          >
                            {isActive ? "Reload" : "Load"}
                          </button>
                          <button
                            type="button"
                            className={styles.btnDanger}
                            onClick={() => handleDelete(name)}
                            disabled={saving}
                            title="Delete this saved set"
                          >
                            ×
                          </button>
                        </div>
                      </li>
                    );
                  })}
              </ul>
            )}
          </section>

          <section className={styles.section}>
            <div className={styles.sectionLabel}>Other</div>
            <div className={styles.actionRow}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={handleNew}
                title="Clear chart and start fresh (untracked)"
              >
                New
              </button>
              <button
                type="button"
                className={styles.btnDanger}
                onClick={handleDeleteAllSaved}
                disabled={saving || Object.keys(sets).length === 0}
                title="Delete every saved set for this symbol from the server"
              >
                Delete all saved sets
              </button>
            </div>
          </section>

          {error ? (
            <div className={styles.error} role="alert">
              {error}
            </div>
          ) : null}
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={onClose}
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
